import { type ReactNode, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  List,
  Paper,
  Progress,
  Select,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client";
import { type Preflight, shows, video } from "../shows/api";
import { type DeviceFailure, listDevices } from "./devices";
import type { PublishStats } from "./stats";
import { type PublishState, usePublisher } from "./usePublisher";
import styles from "./Broadcast.module.scss";

/**
 * The broadcast panel (spec §17.3) — where a host points a camera at a show and where
 * `R-HOST-11`'s pre-flight check is presented.
 *
 * > *Going live broken is the single most damaging operational failure available, and it is almost
 * > entirely preventable.*
 *
 * Five things have to be true, and this card shows all five at once rather than failing at
 * whichever is checked first:
 *
 * | Check | Requirement | Where the answer comes from |
 * | --- | --- | --- |
 * | A supported browser | `R-HOST-10` | `support.ts`, before anything is opened |
 * | A camera, previewed | `R-HOST-11` | The host can see themselves |
 * | A microphone that is hearing something | `R-HOST-11` | A meter that moves, not a device in a list |
 * | Something publishing to the stage | `R-HOST-11` | IVS, via `GET /api/shows/{id}/preflight` |
 * | A valid lot list | `R-HOST-9` | The same route, from the database |
 *
 * The last two are the server's and are the ones `POST .../live` enforces; the first three are the
 * browser's and cannot be enforced anywhere else. A host who sees all five green and presses *go
 * live* is not taking anybody's word for it.
 *
 * ## Why the go-live button is not here
 *
 * It stays with the other show actions at the top of the page, where a host already looks for it.
 * What this card does is tell the page whether it should be enabled — `onPreflight` — so that the
 * control and the reason it is disabled are not in two places arguing.
 */

/** While the host is setting up, the server's half is re-read at this interval. */
const POLL_MS = 3_000;

/**
 * The devices a host can actually choose between.
 *
 * `enumerateDevices` answers before permission with every id and label blanked, so a list of two
 * cameras can contain nothing selectable at all. Filtering on the id is what separates *there is
 * hardware here* from *you may choose it*, and the two are shown differently: the checklist uses
 * the first, the picker the second.
 */
function selectable(devices: MediaDeviceInfo[], fallbackLabel: string) {
  return devices
    .filter((device) => device.deviceId !== "")
    .map((device) => ({
      value: device.deviceId,
      label: device.label || fallbackLabel,
    }));
}

export interface BroadcastProps {
  showId: string;
  live: boolean;
  /** Lifted so the show's own *go live* control can be gated on it (`R-HOST-11`). */
  onPreflight: (checks: Preflight | undefined) => void;
  /**
   * Whether **this browser** is the one publishing, lifted so the stream monitor can stay out of
   * its way (`R-VID-9`, `monitor/Monitor.tsx`).
   *
   * Not the same question as `checks.publishing`, which is the server's answer and is true when
   * a phone somewhere else is the camera. This is *am I the camera*, and it is the only one that
   * decides whether subscribing to the show from here would be a second video of a picture this
   * page already has, encoded on a device that is already encoding, next to a live microphone.
   */
  onPublishing: (publishing: boolean) => void;
  /**
   * Presenter mode (`presenter/PresenterOverlay.tsx`): the picture fills the screen and `overlay`
   * is drawn over it. The same video element, restyled, rather than a second one — so nothing is
   * re-attached, re-subscribed or re-published by entering or leaving it.
   */
  presenting?: boolean;
  overlay?: ReactNode;
  /** Opens presenter mode. Offered beside *Stop broadcasting* while live and publishing here. */
  onPresent?: () => void;
}

export function Broadcast({
  showId,
  live,
  onPreflight,
  onPublishing,
  presenting = false,
  overlay,
  onPresent,
}: BroadcastProps) {
  const { t } = useTranslation();
  const [checks, setChecks] = useState<Preflight | undefined>(undefined);
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  /**
   * What this machine has, read **before** any permission is asked for.
   *
   * `enumerateDevices` answers without permission, with the labels blanked, which is enough to
   * tell *no camera on this machine* from *the camera has not been allowed yet*. Those are two
   * different problems with nothing in common, and a host who is told the wrong one goes looking
   * through browser settings for a webcam that is switched off at the hardware level.
   */
  const [present, setPresent] = useState<{ camera: boolean; microphone: boolean }>();

  /**
   * How far the customer's player turns this show's video (`R-VID-8`).
   *
   * **The server owns it** — it is a column on the show, so it survives a reload, and it can be
   * set from another device — and the pre-flight poll is what carries it here. `turning` holds the
   * value optimistically while the write is in flight, because the poll is seconds away and a
   * button that does nothing visible for three seconds is a button a host presses four times.
   */
  const [turns, setTurns] = useState(0);
  const [turning, setTurning] = useState(false);

  /**
   * **Somebody else is broadcasting this show.**
   *
   * The arrangement `docs/runbooks/video.md` §2 describes is an Android phone as the camera and a laptop
   * driving the running order, so on the laptop this is the ordinary case rather than an
   * exception — and it decides two things. The camera is **not** opened by itself, because a webcam
   * light coming on unasked is at best a surprise and at worst an invitation to publish a second
   * picture into the same show. And the card shows what is happening elsewhere instead of a
   * preview and a *Start broadcasting* button.
   *
   * **There is no *take over*** (`R-VID-39`). There was one, and on 21 October 2026 a second panel
   * publishing beside the phone for a whole show is what it — or a *Start* pressed in the seconds
   * before this notice appeared — led to. One device broadcasts at a time and the server enforces
   * it on the token; to move the camera, stop on the device that has it. A phone that died cannot
   * press *Stop*, and the server lets the next device in once IVS stops listing it as publishing,
   * which is also when this notice clears and the controls come back.
   *
   * `elsewhere` is the *server's* answer — another device holds the broadcast and something is
   * publishing (`packages/core/src/video/preflight.ts`). It used to be `publishing` alone, which
   * told the phone that had just pressed *Stop* that another device was broadcasting, because IVS
   * lists a publisher for a few seconds after it stops. The poll sends this panel's device, so its
   * own broadcast is never reported back to it as somebody else's.
   */
  const elsewhere = checks?.elsewhere ?? false;

  /**
   * Destructured rather than kept as one object, and it is not a style preference: passing any
   * property of an object to a `ref` prop marks the whole object as ref-like to React's own lint
   * rules, and every other property read of it during render then reports as a ref access. Taking
   * the pieces out here keeps that confined to `attachPreview`, which genuinely is one.
   */
  const {
    support: browser,
    cameras,
    microphones,
    cameraId,
    microphoneId,
    choose,
    attachPreview,
    opened,
    cameraOn,
    microphoneOn,
    cameraFailure,
    microphoneFailure,
    level,
    heard,
    meterAwake,
    microphoneMuted,
    state,
    stats,
    subscribers,
    backgrounded,
    mirrored,
    screenAwake,
    failure,
    open,
    start,
    stop,
    device,
    forgetRefusal,
  } = usePublisher(showId, {
    // Not before the first answer: until then `elsewhere` reads false whatever the truth is, and
    // the laptop's webcam would come on while the phone is already the camera.
    autoOpen: checks !== undefined && !elsewhere,
  });

  /**
   * Told upward whenever it moves, so `ShowPage` can mount or unmount the monitor.
   *
   * Everything but `idle` and `failed` counts as *this browser is the camera*: a publish that is
   * starting or reconnecting is one that is about to put a picture on the stage, and flickering
   * a second video in during the gap would be worse than not showing one.
   */
  const publishingHere = state !== "idle" && state !== "failed";
  useEffect(() => {
    onPublishing(publishingHere);
  }, [publishingHere, onPublishing]);
  /** Another device is the camera and this one is not: no preview, and nothing to start. */
  const otherDevice = elsewhere && !publishingHere;
  /**
   * A refusal (*another device is broadcasting*) is cleared whenever the server's answer moves: to
   * `true`, the notice and badge say the same thing better; back to `false`, the other device has
   * stopped and the controls are back, so a red message about it would be stale.
   */
  useEffect(() => {
    forgetRefusal();
  }, [elsewhere, forgetRefusal]);
  /**
   * Polled rather than read once. The two facts on it move for reasons this component cannot see:
   * somebody else edits a lot, or the publish starts a second after the request that asked about
   * it. Stopping the polling when the show is live and publishing would be a small saving and
   * would leave the panel showing a stale answer at the one moment it is being read.
   */
  useEffect(() => {
    let alive = true;

    async function read() {
      try {
        const answer = await video.preflight(showId, device);
        if (!alive) return;
        setChecks(answer.preflight);
        onPreflight(answer.preflight);
        // Not while a write is in flight: the poll would answer with the old angle and undo the
        // optimistic value under the host's finger.
        if (!turning) setTurns(answer.videoRotation);
        setServerError(undefined);
      } catch (failure) {
        if (!alive) return;
        setServerError(
          failure instanceof ApiError ? failure.message : t("broadcast.unreachable"),
        );
      }
    }

    void read();
    const timer = setInterval(() => void read(), POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
      onPreflight(undefined);
    };
  }, [showId, device, onPreflight, turning, t]);

  /**
   * A quarter turn more (`R-VID-8`).
   *
   * Written to the show rather than applied to the camera, which is the whole design: the stream
   * is published as the camera produced it and every viewer's player turns the picture, so the
   * broadcasting machine never re-encodes a frame. The route publishes `show.updated`, so a room
   * already open follows within the couple of seconds spec §11 gives a show-level change — no
   * reload, and nothing to do if a show is already live.
   */
  async function rotate(): Promise<void> {
    const next = (turns + 1) % 4;
    setTurns(next);
    setTurning(true);
    try {
      const answer = await shows.edit(showId, { videoRotation: next });
      setTurns(answer.show.videoRotation);
      setServerError(undefined);
    } catch (failure) {
      // Put back what the server still holds rather than leaving the control lying: a host who
      // thinks the picture is upright and is not has been told something false.
      setTurns(turns);
      setServerError(
        failure instanceof ApiError ? failure.message : t("broadcast.unreachable"),
      );
    } finally {
      setTurning(false);
    }
  }

  useEffect(() => {
    let alive = true;
    void listDevices().then((lists) => {
      if (!alive) return;
      setPresent({
        camera: lists.cameras.length > 0,
        microphone: lists.microphones.length > 0,
      });
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!browser.ok) {
    // `R-HOST-10` — *stated plainly to hosts rather than discovered on the night.* No pickers, no
    // preview, no start button: there is nothing here this browser can do.
    return (
      <Paper withBorder radius="lg" p="lg" className={styles.card}>
        <Alert color="red" variant="light" title={t("broadcast.unsupported.title")}>
          {/*
            Three sentences for three remedies, and the middle one is the reason this is not a
            boolean: *use Chrome or Edge* is a dead end on an iPhone, where no such browser
            exists (`support.ts`). A host who follows that advice installs Chrome, is refused
            again, and stops trusting the panel.
          */}
          {browser.reason === "browser"
            ? t("broadcast.unsupported.browser")
            : browser.reason === "apple-mobile"
              ? t("broadcast.unsupported.apple-mobile")
              : t("broadcast.unsupported.capabilities")}
        </Alert>
      </Paper>
    );
  }

  const publishing = state === "publishing";

  return (
    <Paper withBorder radius="lg" p="lg" className={styles.card}>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <Text fw={650}>{t("broadcast.heading")}</Text>
            {/*
              The show's broadcast, not only this browser's. A panel watching another device be the
              camera used to read *Not broadcasting* beside a notice saying the show was being
              broadcast — both true of this browser, and the badge is what people read first.
            */}
            <Badge
              variant="light"
              color={
                publishing || otherDevice
                  ? "green"
                  : state === "reconnecting"
                    ? "orange"
                    : state === "failed"
                      ? "red"
                      : "gray"
              }
            >
              {otherDevice
                ? t("broadcast.state.elsewhere")
                : t(`broadcast.state.${state}`)}
            </Badge>
            {stats ? (
              <Badge
                variant="light"
                color={
                  stats.health === "good"
                    ? "green"
                    : stats.health === "fair"
                      ? "orange"
                      : stats.health === "poor"
                        ? "red"
                        : "gray"
                }
              >
                {t(`broadcast.health.${stats.health}`)}
              </Badge>
            ) : null}
            {subscribers !== undefined ? (
              <Badge variant="light" color="gray">
                {t("broadcast.watching", { count: subscribers })}
              </Badge>
            ) : null}
          </Group>
          <Group gap="xs">
            {/*
              Offered until both devices are open, and offered *again* after a failure — this is
              the retry. A host who allows the camera in the address bar after refusing it has no
              other way back, and without this the card would be a dead end.
            */}
            {(cameraOn && microphoneOn) || otherDevice ? null : (
              <Button
                size="compact-sm"
                variant={opened ? "default" : "filled"}
                onClick={() => void open()}
              >
                {t(
                  opened || cameraFailure || microphoneFailure
                    ? "broadcast.retry"
                    : "broadcast.open",
                )}
              </Button>
            )}
            {/*
              **Disabled until there is something to send**, with the reason in a tooltip rather
              than left to be discovered by pressing it. A button that appears to work and does
              nothing is the worst of the three states it could be in.

              What it is *not* gated on is the rest of the checklist, and the reason is worth
              stating: *your video is reaching the show* only becomes true **because** this was
              pressed, so requiring it first would be a button that can never be enabled. The lot
              list is not a precondition either — a host may broadcast while somebody else fixes a
              lot; it is *going live* that both of those gate, which is `R-HOST-11` and is the
              control at the top of the page.
            */}
            {/*
              Not offered while another device is the camera (`R-VID-39`). The server would refuse
              it anyway; a button whose only possible outcome is a refusal is not one to show.
            */}
            {!publishing && state !== "reconnecting" && !otherDevice ? (
              <Tooltip
                label={opened ? t("broadcast.startReady") : t("broadcast.startBlocked")}
                withArrow
              >
                <Button
                  size="compact-sm"
                  loading={state === "starting"}
                  data-disabled={opened ? undefined : true}
                  onClick={() => {
                    if (opened) void start();
                  }}
                >
                  {t("broadcast.start")}
                </Button>
              </Tooltip>
            ) : null}
            {live && publishing && onPresent ? (
              <Button size="compact-sm" variant="default" onClick={onPresent}>
                {t("presenter.enter")}
              </Button>
            ) : null}
            {publishing || state === "reconnecting" ? (
              <Button size="compact-sm" variant="default" onClick={stop}>
                {t("broadcast.stop")}
              </Button>
            ) : null}
          </Group>
        </Group>

        {otherDevice ? (
          <Alert color="green" variant="light" radius="md">
            <Text size="sm">{t("broadcast.elsewhere")}</Text>
          </Alert>
        ) : null}

        <Group
          align="flex-start"
          gap="lg"
          wrap="wrap"
          // Hidden rather than unmounted while another device is the camera: unmounting would drop
          // the preview element, and broadcasting from here once that device stops should not have
          // to rebuild it from nothing.
          display={otherDevice ? "none" : undefined}
        >
          {/*
            The host's own camera, straight from `getUserMedia` and never routed through the stage.
            Muted — an unmuted preview of your own microphone is a feedback loop — and mirrored for
            a self view, because a preview that is not mirrored makes aiming a camera at yourself
            unexpectedly hard. See `data-mirrored` below for when it is not.
          */}
          <div
            className={styles.previewFrame}
            data-presenting={presenting || undefined}
          >
            <video
              ref={attachPreview}
              className={styles.preview}
              // No `data-live` here, deliberately. The preview is the same size before the show
              // and during it — see the note in the stylesheet for why it does not shrink.
              // Mirrored for a self view and not for a back camera: a mirrored product has its
              // packaging text backwards. What is published is never mirrored either way.
              // **Mirrored only while the picture is not turned.**
              //
              // The mirror exists so somebody can aim a camera at themselves, and it is applied
              // whenever the track does not say `environment` — which a webcam never does. Once
              // the picture is turned that guess becomes actively wrong: a left-right mirror of
              // content that has been rotated a quarter turn is a flip about the content's *own*
              // vertical axis, so the preview would show the host upside down relative to what
              // every viewer is getting. A host who has mounted a camera on its side has a rig aimed
              // at products, not a self view, so there is nothing to mirror either way.
              data-mirrored={mirrored && turns === 0 ? true : undefined}
              // The same CSS treatment the room gives it, so the host frames against what
              // viewers see. Absent rather than `0`, so an unturned preview matches no rule.
              data-turns={turns === 0 ? undefined : turns}
              muted
              autoPlay
              playsInline
            />
            {presenting ? overlay : null}
          </div>

          <Stack gap="sm" className={styles.controls}>
            {/*
              Selectable devices only. Before permission is granted the browser answers with
              blank ids, and an option whose value is `""` is one that cannot be chosen — so the
              picker is enabled by whether there is anything real in it rather than by whether the
              last open succeeded. Gating on the last open would leave both of these disabled with
              no way out when a single failed request is the only thing that has ever filled them.
            */}
            <Select
              size="xs"
              label={t("broadcast.camera")}
              data={selectable(cameras, t("broadcast.unnamedCamera"))}
              value={cameraId ?? null}
              onChange={(value) => value && choose("camera", value)}
              disabled={selectable(cameras, "").length === 0}
              allowDeselect={false}
            />

            {/*
              `R-VID-8` — *portrait is the primary format*, and a camera on a tripod is landscape.
              Turning the camera on its side is the photographer's answer and this is the software
              half: a quarter turn puts the sideways sensor image upright in a 720 × 1280 frame,
              nothing cropped.

              Directly under the camera picker, because it is a property of the camera rather than
              of the show, and it is set once while setting up. The angle is on the button rather
              than in a label — a control that says what it will do next and what state it is in
              costs one line instead of two.
            */}
            <Group gap="xs" align="center" wrap="nowrap">
              <Button
                size="compact-xs"
                variant="default"
                onClick={() => void rotate()}
                loading={turning}
                // Not gated on a camera: the angle belongs to the show, so a laptop driving a
                // show that a phone is the camera for can still correct a sideways picture.
                disabled={turning}
              >
                {t("broadcast.rotate")}
              </Button>
              <Text size="xs" c="dimmed">
                {t(`broadcast.rotated.${turns}`)}
              </Text>
            </Group>
            <Stack gap={4}>
              <Select
                size="xs"
                label={t("broadcast.microphone")}
                data={selectable(microphones, t("broadcast.unnamedMicrophone"))}
                value={microphoneId ?? null}
                onChange={(value) => value && choose("microphone", value)}
                disabled={selectable(microphones, "").length === 0}
                allowDeselect={false}
              />
              {/* The evidence that a microphone works, rather than that one is selected. */}
              <Progress
                value={level * 100}
                size="sm"
                color={heard ? "green" : "gray"}
                transitionDuration={80}
              />
              {/*
                Three unmet states, not one. *No microphone*, *the browser will not let us measure
                one yet*, and *say something* are three different facts, and the middle one is not
                a fault at all — it is a suspended `AudioContext`, which the first interaction with
                the page clears. Collapsing it into "say something" is how a host ends up shouting
                at a microphone that was working the whole time.
              */}
              <Text size="xs" c={heard ? "dimmed" : "orange"}>
                {!microphoneOn
                  ? t("broadcast.noMicrophoneYet")
                  : microphoneMuted
                    ? t("broadcast.systemMuted")
                    : heard
                      ? t("broadcast.heard")
                      : meterAwake
                        ? t("broadcast.saySomething")
                        : t("broadcast.wakeMeter")}
              </Text>
            </Stack>

            {/* Under the devices, in the same column: the camera and microphone are what a host is
             *setting up*, and this is how it is *going* — the same conversation, in order. */}
            {stats ? <StreamFigures stats={stats} /> : null}
          </Stack>
        </Group>

        {/* `R-HOST-13` — a backgrounded tab is throttled and a sleeping machine drops the publish. */}
        {backgrounded && publishing ? (
          <Alert color="orange" variant="light" radius="md">
            {t("broadcast.backgrounded")}
          </Alert>
        ) : null}

        {/*
          Said only when it is *not* being done for them. A held wake lock needs no announcement —
          nothing is being asked of the host — but a refused one moves the screen timeout back into
          their hands, and on a phone that is the difference between a show and a two-minute show.
        */}
        {publishing && !screenAwake ? (
          <Alert color="orange" variant="light" radius="md">
            {t("broadcast.screenMaySleep")}
          </Alert>
        ) : null}

        {/*
          One alert per device, because they fail independently and for different reasons. A
          missing microphone and a blocked camera on the same machine are two remedies, and asking
          for both devices in one request could only ever produce one message about a request
          rather than about a device (`devices.ts`).
        */}
        {cameraFailure ? (
          <Alert
            color={cameraFailure === "missing" ? "orange" : "red"}
            variant="light"
            radius="md"
          >
            {t(`broadcast.deviceFailure.${cameraFailure}`, {
              device: t("broadcast.deviceName.camera"),
            })}
          </Alert>
        ) : null}
        {microphoneFailure ? (
          <Alert
            color={microphoneFailure === "missing" ? "orange" : "red"}
            variant="light"
            radius="md"
          >
            {t(`broadcast.deviceFailure.${microphoneFailure}`, {
              device: t("broadcast.deviceName.microphone"),
            })}
          </Alert>
        ) : null}
        {/* Louder than a caption, because this one silences the show rather than the meter. */}
        {microphoneMuted ? (
          <Alert color="red" variant="light" radius="md">
            {t("broadcast.systemMutedAlert")}
          </Alert>
        ) : null}
        {/* `R-VID-7` — audio matters more than video, so a voice with no picture is a warning and
            not a refusal. The host is told what viewers will get. */}
        {microphoneOn && !cameraOn && !cameraFailure ? (
          <Alert color="orange" variant="light" radius="md">
            {t("broadcast.audioOnly")}
          </Alert>
        ) : null}
        {failure ? (
          <Alert color="red" variant="light" radius="md">
            {failure}
          </Alert>
        ) : null}

        {serverError ? (
          <Alert color="orange" variant="light" radius="md">
            {serverError}
          </Alert>
        ) : null}

        {live ? null : (
          <Checklist
            browser={browser.ok}
            camera={cameraOn}
            cameraFailure={cameraFailure}
            microphone={heard}
            microphoneOn={microphoneOn}
            meterAwake={meterAwake}
            microphoneMuted={microphoneMuted}
            microphoneFailure={microphoneFailure}
            present={present}
            publishState={state}
            checks={checks}
          />
        )}
      </Stack>
    </Paper>
  );
}

/**
 * What the publish is doing, while it is doing it.
 *
 * A host presenting for two hours has no other way to know their stream is degrading: the picture
 * beside this is their *camera*, which looks perfect while the upstream falls apart. So the figures
 * are the ones that change when something is going wrong, and each is labelled with what it is
 * rather than with a WebRTC field name.
 *
 * **These are upstream figures.** The round trip is this machine to IVS — a component of
 * `R-VID-3`'s glass-to-glass latency and typically the smaller half, not the thing itself — and
 * nothing here is `R-VID-9`'s *what viewers are actually seeing*, which needs a subscribing
 * connection — that is the stream monitor (`monitor/Monitor.tsx`), shown to everybody but the
 * presenter. The label says *to IVS* for that reason: a figure on a screen will be
 * quoted, and this one must not be quoted as the show's latency.
 */
function StreamFigures({ stats }: { stats: PublishStats }) {
  const { t } = useTranslation();

  const figures: { key: string; value: string; warn?: boolean }[] = [];

  if (stats.width && stats.height) {
    figures.push({
      key: "resolution",
      value: `${stats.width}×${stats.height}${stats.fps ? ` · ${stats.fps} fps` : ""}`,
    });
  }
  if (stats.videoKbps !== undefined) {
    figures.push({
      key: "bitrate",
      value: t("broadcast.figures.kbps", { value: stats.videoKbps }),
      // Below roughly a quarter-megabit the picture is visibly poor whatever the cause.
      warn: stats.videoKbps < 250,
    });
  }
  if (stats.roundTripMs !== undefined) {
    figures.push({
      key: "roundTrip",
      value: t("broadcast.figures.ms", { value: stats.roundTripMs }),
      warn: stats.roundTripMs >= 200,
    });
  }
  if (stats.lossPercent !== undefined) {
    figures.push({
      key: "loss",
      value: t("broadcast.figures.percent", { value: stats.lossPercent }),
      warn: stats.lossPercent >= 1,
    });
  }
  if (stats.availableKbps !== undefined) {
    figures.push({
      key: "headroom",
      value: t("broadcast.figures.kbps", { value: stats.availableKbps }),
    });
  }

  return (
    <Stack gap={6}>
      {/* Separated, because it belongs to the column above it without being part of the pickers. */}
      <Divider />
      {/* `md` rather than `lg`: this sits in a narrow column beside the preview and wraps into two
          or three short rows, which reads better than one long line that overflows it. */}
      <Group gap="md" wrap="wrap">
        {figures.map((figure) => (
          <Stack gap={0} key={figure.key}>
            <Text size="xs" c="dimmed">
              {t(`broadcast.figures.${figure.key}`)}
            </Text>
            <Text size="sm" fw={600} c={figure.warn ? "orange" : undefined}>
              {figure.value}
            </Text>
          </Stack>
        ))}
      </Group>
      {/* Said in words, because a number nobody can act on is worse than a sentence. */}
      {stats.limitedBy ? (
        <Text size="xs" c="orange">
          {t(
            stats.limitedBy === "cpu"
              ? "broadcast.figures.limitedByCpu"
              : "broadcast.figures.limitedByBandwidth",
          )}
        </Text>
      ) : null}
      {stats.relayed ? (
        <Text size="xs" c="dimmed">
          {t("broadcast.figures.relayed")}
        </Text>
      ) : null}
    </Stack>
  );
}

/**
 * `R-HOST-11` as a list a host can read in the ten seconds before they start.
 *
 * Shown only while the show is not yet live. Once it is, the pre-flight has served its purpose and
 * what matters is the state badge above — a checklist on a running show is furniture.
 */
function Checklist({
  browser,
  camera,
  cameraFailure,
  microphone,
  microphoneOn,
  meterAwake,
  microphoneMuted,
  microphoneFailure,
  present,
  publishState,
  checks,
}: {
  browser: boolean;
  camera: boolean;
  cameraFailure: DeviceFailure | undefined;
  /** Heard, which is the check. `microphoneOn` is only whether a track exists. */
  microphone: boolean;
  microphoneOn: boolean;
  meterAwake: boolean;
  microphoneMuted: boolean;
  microphoneFailure: DeviceFailure | undefined;
  /** What the machine has, read before permission. `undefined` until the first enumeration. */
  present: { camera: boolean; microphone: boolean } | undefined;
  /** What *this browser* believes about its own publish, which is a different fact from below. */
  publishState: PublishState;
  checks: Preflight | undefined;
}) {
  const { t } = useTranslation();

  /**
   * What to say about a device that is not working yet, in order of usefulness: the machine does
   * not have one, the browser will not give it to us, or nobody has asked for it yet. The third is
   * the ordinary state of this card before a host presses anything, and calling it a problem would
   * make the checklist cry wolf on every page load.
   */
  const deviceNote = (
    failure: DeviceFailure | undefined,
    exists: boolean | undefined,
  ) =>
    t(`broadcast.check.state.${failure ?? (exists === false ? "missing" : "notYet")}`);

  /**
   * **Three tones, not two.** A row that is not ticked is either *something is wrong* or *you have
   * not done it yet*, and those are not the same message: the first wants a warning sign, and the
   * second wants telling apart from one. With two tones, a host who has simply not pressed *Start
   * broadcasting* is shown a ⚠ beside a camera that is working perfectly, which is the panel
   * crying wolf on its own ordinary state.
   *
   * `problem` is reserved for something a host has to act on — a device that is missing, blocked or
   * muted outside the browser, a lot that cannot be sold. Everything else on the way to going live
   * is `pending`, and reads as information.
   */
  type Tone = "done" | "pending" | "problem";

  const deviceTone = (
    ok: boolean,
    failure: DeviceFailure | undefined,
    exists: boolean | undefined,
  ): Tone => {
    if (ok) return "done";
    // A device that is absent, refused or held by something else is a problem. One nobody has
    // asked for yet is not.
    return failure !== undefined || exists === false ? "problem" : "pending";
  };

  const items: { key: string; tone: Tone; note?: string; hint?: string }[] = [
    { key: "browser", tone: browser ? "done" : "problem" },
    {
      key: "camera",
      tone: deviceTone(camera, cameraFailure, present?.camera),
      note: camera ? undefined : deviceNote(cameraFailure, present?.camera),
    },
    {
      key: "microphone",
      // A microphone muted outside the browser *is* a problem — it publishes silence — where one
      // that simply has not been spoken into yet is not.
      tone: microphone
        ? "done"
        : microphoneOn && microphoneMuted
          ? "problem"
          : deviceTone(false, microphoneFailure, present?.microphone),
      note: microphone
        ? undefined
        : // Three ways an open microphone can fail to be *heard*, and they need three answers:
          // something outside the browser has muted it, the level cannot be measured yet, or the
          // room is quiet. Only the last one is answered by talking.
          microphoneOn && microphoneMuted
          ? t("broadcast.check.state.systemMuted")
          : microphoneOn && !meterAwake
            ? t("broadcast.check.state.notMeasurable")
            : deviceNote(microphoneFailure, present?.microphone),
    },
    {
      key: "publishing",
      /**
       * **Never a problem, only pending.** Not having started broadcasting is the state this panel
       * opens in, and the row exists to say *what is left*, not to accuse anybody. The one case that
       * warrants a warning is a publish that tried and failed, which has its own alert above anyway.
       */
      tone: checks?.publishing
        ? "done"
        : publishState === "failed"
          ? "problem"
          : "pending",
      /**
       * **Two different facts, and the row has to say which one is missing.**
       *
       * `ok` is the *server's* answer — IVS asked whether anybody is publishing on this show's
       * stage — and it is the one that gates going live. What this browser believes about its own
       * publish is separate, and when the two disagree the disagreement is the useful thing to
       * report: *this browser is publishing and the show has not confirmed it yet* is a different
       * situation from *nothing is publishing*, and only the second is answered by pressing a
       * button. The first is either propagation lag — the panel polls every three seconds — or a
       * real problem, and a bare warning sign tells a host neither.
       */
      hint:
        publishState === "publishing"
          ? t("broadcast.check.notConfirmed")
          : publishState === "starting" || publishState === "reconnecting"
            ? t("broadcast.check.connecting")
            : t("broadcast.check.startFirst"),
      // The one check that can be *waived* rather than passed: if IVS cannot be reached the show
      // is allowed to go live anyway, and a host is told which of the two happened rather than
      // being shown a tick that means "we did not ask".
      note:
        checks && !checks.streamKnown ? t("broadcast.check.streamUnknown") : undefined,
    },
    {
      key: "lots",
      // A lot that cannot be sold is a problem; a check that has not answered yet is pending.
      tone: checks ? (checks.problems.length === 0 ? "done" : "problem") : "pending",
      hint: t("broadcast.check.lotsHint"),
    },
  ];

  return (
    <Stack gap={6}>
      <Text size="xs" tt="uppercase" fw={700} c="dimmed">
        {t("broadcast.preflight")}
      </Text>
      <List size="sm" spacing={2} listStyleType="none">
        {items.map((item) => {
          /**
           * The glyph carries the meaning and the colour agrees with it, rather than the colour
           * carrying it alone — which is what keeps the list readable to somebody who cannot
           * distinguish orange from grey (`R-A11Y-3`'s reasoning, though the pass itself is M3.4).
           */
          const note = item.note ?? (item.tone === "done" ? undefined : item.hint);
          const glyph =
            item.tone === "done" ? "✓" : item.tone === "problem" ? "⚠" : "•";
          return (
            <List.Item key={item.key}>
              <Text
                size="sm"
                c={
                  item.tone === "problem"
                    ? "orange"
                    : item.tone === "pending"
                      ? "dimmed"
                      : undefined
                }
                fw={item.tone === "problem" ? 500 : undefined}
              >
                {glyph} {t(`broadcast.check.${item.key}.label`)}
                {note ? ` — ${note}` : ""}
              </Text>
            </List.Item>
          );
        })}
      </List>
      {checks && checks.lots === 0 ? (
        <Text size="xs" c="dimmed">
          {t("broadcast.check.noLots")}
        </Text>
      ) : null}
      {checks?.problems.length ? (
        <List size="sm" spacing={2}>
          {checks.problems.map((problem) => (
            <List.Item key={`${problem.lotId}-${problem.code}`}>
              <Text size="sm" c="red">
                {t("broadcast.check.lotProblem", {
                  position: problem.position + 1,
                  message: problem.message,
                })}
              </Text>
            </List.Item>
          ))}
        </List>
      ) : null}
    </Stack>
  );
}
